import { type PagePostResponse, type PostResponse } from '../../types'

const SORTS_THE_PATCH_CAN_PLACE_IN = ['created_at,desc']

export const CAN_BE_PLACED_IN_THE_PUBLISHED_LIST = ['SENT', 'PUBLISHING']

const STILL_BELONGS_IN_THE_PUBLISHED_LIST = [...CAN_BE_PLACED_IN_THE_PUBLISHED_LIST, 'PROCESSING']

const filterMatches = (filter: string | null, value: string | undefined): boolean =>
  filter === null || filter === value

export const belongsToCachedList = (args: string, post: PostResponse): boolean => {
  const params = new URLSearchParams(args)
  const listStatus = params.get('status')
  const status = post.status

  const statusFits =
    status === undefined ||
    (listStatus === 'SENT'
      ? STILL_BELONGS_IN_THE_PUBLISHED_LIST.includes(status)
      : listStatus === status)

  return (
    statusFits &&
    filterMatches(params.get('postType'), post.postType) &&
    filterMatches(params.get('accountId'), post.socialAccount?.id) &&
    filterMatches(params.get('campaignId'), post.campaign?.id)
  )
}

export const matchesCachedList = (args: string, post: PostResponse): boolean => {
  const params = new URLSearchParams(args)
  const page = params.get('page')
  const sorts = params.getAll('sort')

  return (
    params.get('status') === 'SENT' &&
    (page === null || page === '0') &&
    post.status !== undefined &&
    CAN_BE_PLACED_IN_THE_PUBLISHED_LIST.includes(post.status) &&
    !params.has('search') &&
    filterMatches(params.get('postType'), post.postType) &&
    filterMatches(params.get('accountId'), post.socialAccount?.id) &&
    filterMatches(params.get('campaignId'), post.campaign?.id) &&
    sorts.length <= 1 &&
    (sorts.length === 0 || SORTS_THE_PATCH_CAN_PLACE_IN.includes(sorts[0]))
  )
}

export const applyPostToPage = (page: PagePostResponse, post: PostResponse): boolean => {
  const content = page.content ?? []
  const alreadyThere = content.findIndex(({ id }) => id === post.id)

  if (alreadyThere !== -1) {
    content[alreadyThere] = post
    return true
  }

  const createdAt = post.createdAt

  if (createdAt === undefined) {
    return false
  }

  const placeFor = content.findIndex(
    (row) => row.createdAt !== undefined && row.createdAt < createdAt,
  )
  const belongsOnALaterPage = placeFor === -1 && content.length > 0 && page.last !== true

  if (belongsOnALaterPage) {
    return false
  }

  content.splice(placeFor === -1 ? content.length : placeFor, 0, post)

  const totalElements = (page.totalElements ?? content.length - 1) + 1
  const size = page.size ?? content.length

  page.content = content.slice(0, size)
  page.totalElements = totalElements
  page.totalPages = Math.ceil(totalElements / size)
  page.numberOfElements = page.content.length
  page.empty = page.content.length === 0
  page.last = (page.number ?? 0) >= page.totalPages - 1

  return true
}

export const removePostFromPage = (page: PagePostResponse, postId: string): boolean => {
  const content = page.content
  const at = content?.findIndex(({ id }) => id === postId) ?? -1

  if (content === undefined || at === -1) {
    return false
  }

  content.splice(at, 1)

  page.totalElements = Math.max(0, (page.totalElements ?? content.length + 1) - 1)
  page.numberOfElements = content.length
  page.empty = content.length === 0

  return true
}
